import { ref, computed, type Ref } from 'vue'
import { type Workflow, WorkflowStatus } from '@/types'
import type { WorkflowVersionSummary } from '@/queries/workflows'

export interface WorkflowVersion {
  /** Real WorkflowVersion id — present on published entries, undefined for the synthetic Draft head. */
  id?: string
  version: string
  name: string
  description: string
  status: 'draft' | 'published' | 'archived'
  isReadOnly: boolean
}

/**
 * @param workflow the workflow being edited (its Status drives dirty/publish state)
 * @param publishedVersionSummaries real published versions, newest first (from useWorkflowVersions)
 */
export function useVersionManagement(
  workflow: Ref<Workflow | undefined>,
  publishedVersionSummaries: Ref<WorkflowVersionSummary[]>
) {
  const selectedVersion = ref('Draft')

  // Published version labels, newest first, sourced from the real WorkflowVersion documents.
  const publishedVersions = computed<string[]>(() =>
    publishedVersionSummaries.value.map(v => v.versionLabel)
  )

  // Structured version list: Draft first, then published (most recent first). The newest published
  // version is the live one; older ones are archived (read-only history).
  const versions = computed<WorkflowVersion[]>(() => {
    const draft: WorkflowVersion = {
      version: 'Draft',
      name: 'Draft',
      description: 'Current working draft',
      status: 'draft',
      isReadOnly: false,
    }

    const published: WorkflowVersion[] = publishedVersionSummaries.value.map((v, i) => ({
      id: v.id,
      version: v.versionLabel,
      name: v.versionLabel,
      description: '',
      status: i === 0 ? 'published' : 'archived',
      isReadOnly: true,
    }))

    return [draft, ...published]
  })

  const currentVersion = computed(() =>
    versions.value.find(v => v.version === selectedVersion.value) ?? versions.value[0]
  )

  const isReadOnlyVersion = computed(() => currentVersion.value?.isReadOnly ?? false)

  const isPublishedVersion = computed(() =>
    currentVersion.value?.status === 'published' || currentVersion.value?.status === 'archived'
  )

  // Human label for the version-dropdown button. The working-copy entry is only honestly a "Draft"
  // when the workflow actually has unpublished edits (status === Draft); a freshly-published workflow
  // is clean, so show its published version instead of a misleading "Draft".
  const selectedVersionText = computed(() => {
    const v = currentVersion.value
    if (!v) return selectedVersion.value
    if (v.status === 'draft') {
      if (hasDraftChanges.value) return 'Draft • unpublished changes'
      const published = latestPublishedVersion.value
      return published ? `${published} (Published)` : 'Draft'
    }
    if (v.status === 'published') return `${v.version} (Published)`
    return `${v.version} (Archived)`
  })

  // The version label that will be assigned when the user publishes the current draft.
  const nextPublishVersion = computed(() => workflow.value?.currentVersion ?? 'v1')

  const hasPublishedVersions = computed(() => publishedVersions.value.length > 0)

  const latestPublishedVersion = computed(() => publishedVersions.value[0] ?? null)

  // True only when the workflow has edits awaiting publish. Backend rejects a publish call when
  // status is already Published (no draft changes) — gate the publish UI on this so the user
  // doesn't see a confusing 400.
  const hasDraftChanges = computed(() => workflow.value?.status === WorkflowStatus.Draft)

  function selectVersion(version: WorkflowVersion) {
    selectedVersion.value = version.version
  }

  return {
    selectedVersion,
    versions,
    currentVersion,
    isReadOnlyVersion,
    isPublishedVersion,
    selectedVersionText,
    nextPublishVersion,
    hasPublishedVersions,
    hasDraftChanges,
    latestPublishedVersion,
    publishedVersions,
    selectVersion,
  }
}
